import React, { FunctionComponent as FC, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import { useVideoModal } from '../VideoModal';
import { caseIll } from '../../common/nexusArt';

interface CaseStudy {
  youtubeId: string;
  title: string;      // exactly as the case heading reads in the manuscript
  where: string;      // where it lives in the paper
}

// Six case studies, in the order of the manuscript; videos are hosted on YouTube
const caseStudies: CaseStudy[] = [
  { youtubeId: '4voBIt875ds', title: 'University Innovation Landscape', where: 'Sec. 3.2.1' },
  { youtubeId: 'qB3wovWq0mU', title: 'Use of Science in Public Policy', where: 'Sec. 3.2.2' },
  { youtubeId: 'ESrkofJGIK4', title: 'The Impact of Science Funding', where: 'Sec. 3.2.3' },
  { youtubeId: 'aQsvgHcZM88', title: 'Disruptive Science Across Fields and Nations', where: 'Sec. 3.2.4' },
  { youtubeId: 'RlMw5RRhGEw', title: 'AI-Guided Exploration from Open-Ended Goals', where: 'Supplementary Note 2.5' },
  { youtubeId: 'FYplUCbfKNw', title: 'Artifact and Provenance', where: 'Sec. 3.1' },
];

// muted, chromeless, looping preview; clicks pass through to the card, which opens the full player
const previewUrl = (id: string) =>
  `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&playsinline=1&modestbranding=1&rel=0&disablekb=1&iv_load_policy=3`;

interface CaseStudiesPageProps {
}

const CaseStudiesPage: FC<CaseStudiesPageProps> = () => {
  const { open } = useVideoModal();
  const ills = useMemo(() => caseStudies.map((_, i) => caseIll(i)), []);
  // hover a card → its video plays in place of the illustration (after a short delay, so sweeping past doesn't load six players)
  const [hovered, setHovered] = useState<number | null>(null);
  const hoverT = useRef<ReturnType<typeof setTimeout>>();
  const enter = (i: number) => { clearTimeout(hoverT.current); hoverT.current = setTimeout(() => setHovered(i), 250); };
  const leave = () => { clearTimeout(hoverT.current); setHovered(null); };
  useEffect(() => () => clearTimeout(hoverT.current), []);

  return (
    <section className='cases contain' id='cases'>
      <div className='chead'><h2>Case Studies</h2></div>
      <div className='cgrid' id='cgrid'>
        {caseStudies.map((c, i) => (
          <a
            className='case'
            key={c.youtubeId}
            href={`https://www.youtube.com/watch?v=${c.youtubeId}`}
            target='_blank'
            rel='noopener noreferrer'
            onMouseEnter={() => enter(i)}
            onMouseLeave={leave}
            onClick={(e) => { e.preventDefault(); leave(); open({ source: { kind: 'youtube', id: c.youtubeId }, title: c.title, kicker: `Case study ${String(i + 1).padStart(2, '0')}` }); }}
          >
            <div className='ill'><svg viewBox='0 0 200 120' preserveAspectRatio='none' dangerouslySetInnerHTML={{ __html: ills[i] }} />
              {hovered === i && <iframe className='preview' src={previewUrl(c.youtubeId)} title={c.title} allow='autoplay; encrypted-media' tabIndex={-1} aria-hidden='true' />}
            </div>
            <div className='cap'><b>{c.title}</b><span>Watch ▸</span></div>
          </a>
        ))}
      </div>
    </section>
  )
}

export default CaseStudiesPage;
